import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { debounce } from 'lodash';
import domReady from '../helpers/dom-ready';

class Menu {
  static init() {
    return new Menu();
  }

  constructor() {
    this.menu = document.querySelector('.brix-menu');
    this.logo = document.querySelector('.logo');
    this.toggleMenuButton = this.menu.querySelector('.menu-button');
    this.menuWrapper = this.menu.querySelector('.menu-wrapper');
    this.menuList = this.menu.querySelector('.menu');
    this.menuItemsWithSubMenu = this.menu.querySelectorAll('.-with-sub-menu');

    // eslint-disable-next-line no-multi-assign
    window.onRekAISelected = this.onRekAISelected = (
      event,
      // eslint-disable-next-line no-unused-vars
      suggestion,
      // eslint-disable-next-line no-unused-vars
      dataset,
    ) => {
      this.search(event);
    };

    // Search
    this.searchForm = this.menu.querySelector('.search-form');

    if (this.searchForm) {
      this.toggleSearchButton = this.menu.querySelector(
        '.toggle-search-button',
      );
      this.searchTextInput = this.searchForm.querySelector('.search-input');
      this.searchButton = this.searchForm.querySelector('.search-button');
    }

    // Language switcher
    this.languageSwitcher = this.menu.querySelector(
      '.language-switcher > select',
    );

    this.isMenuOpen = false;
    this.overflowBreakpoint = null;

    this.addEventListeners();
    this.checkOverflow();
    this.checkCookiesForRekAIConsent();
  }

  getCookie(cname) {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  checkCookiesForRekAIConsent() {
    const statisticsCookie = this.getCookie('cmplz_statistics');
    if (statisticsCookie && statisticsCookie === 'allow') {
      sessionStorage.setItem('rekai_consent_accepted', 'true');
    } else {
      sessionStorage.removeItem('rekai_consent_accepted');
    }
  }

  addEventListeners() {
    document.addEventListener('cmplz_status_change', (e) => {
      if (e.detail.category === 'statistics' && e.detail.value === 'allow') {
        sessionStorage.setItem('rekai_consent_accepted', 'true');
      }
      if (e.detail.category === 'statistics' && e.detail.value === 'deny') {
        sessionStorage.removeItem('rekai_consent_accepted');
      }
    });

    window.addEventListener(
      'resize',
      debounce(() => {
        this.checkOverflow();
        this.checkOpenMenu();
      }),
      300,
    );

    window.addEventListener(
      'scroll',
      debounce(() => {
        this.toggleSolidBackground();
      }),
      300,
    );

    window.addEventListener('click', (e) => {
      this.checkClickInMenu(e);
    });

    this.toggleMenuButton.addEventListener('click', () => {
      this.toggleIsMenuOpen();
    });

    this.menu.addEventListener('transitionend', (e) => {
      if (e.target !== this.menu) {
        return;
      }

      if (e.propertyName === 'transform') {
        this.menu.classList.remove('-closing');
      }
    });

    if (this.searchForm) {
      this.toggleSearchButton.addEventListener('click', () => {
        this.toggleIsSearchOpen();
      });

      this.searchButton.addEventListener('click', (e) => {
        this.search(e);
      });
    }

    if (this.languageSwitcher) {
      this.languageSwitcher.addEventListener('change', () => {
        this.changeLanguage();
      });
    }

    if (this.menuItemsWithSubMenu) {
      this.menuItemsWithSubMenu.forEach((subMenuEl) => {
        subMenuEl.addEventListener('click', (e) => {
          this.toggleSubMenu(e);
        });
        subMenuEl.addEventListener('keydown', (e) => {
          if (e.keyCode === 13) {
            this.toggleSubMenu(e);
          }
        });
      });
    }
  }

  checkOverflow() {
    const isOverflowing = this.menuWrapper.clientWidth < this.menuWrapper.scrollWidth;

    if (!this.overflowBreakpoint && isOverflowing) {
      this.overflowBreakpoint = window.innerWidth;
      this.menu.classList.add('-overflow');
    }

    if (
      this.overflowBreakpoint
      && window.innerWidth > this.overflowBreakpoint
    ) {
      this.overflowBreakpoint = null;
      this.menu.classList.remove('-overflow');
    }
  }

  checkOpenMenu() {
    const breakpoint = this.overflowBreakpoint ? this.overflowBreakpoint : 576;

    if (window.innerWidth > breakpoint && this.isMenuOpen) {
      this.toggleIsMenuOpen();
    }
  }

  toggleSolidBackground() {
    if (document.body.classList.contains('-menu-type-transparent')) {
      if (window.scrollY > 50) {
        this.menu.classList.add('-solid-background');
      } else {
        this.menu.classList.remove('-solid-background');
      }
    }
  }

  toggleIsMenuOpen() {
    this.menu.classList.toggle('-open-menu');
    this.isMenuOpen = !this.isMenuOpen;
    this.toggleMenuButton.setAttribute('aria-expanded', this.isMenuOpen);

    if (this.isMenuOpen) {
      disableBodyScroll(this.menu);
      disableBodyScroll(this.menuWrapper);
      if (this.menu.classList.contains('-open-search')) {
        this.menu.classList.toggle('-open-search');
      }
    } else {
      enableBodyScroll(this.menu);
      enableBodyScroll(this.menuWrapper);
      this.menu.classList.add('-closing');
      this.closeAllSubMenus();
    }
  }

  toggleIsSearchOpen() {
    this.menu.classList.toggle('-open-search');
    this.isSearchOpen = this.menu.classList.contains('-open-search');

    if (this.isSearchOpen) {
      if (!this.toggleSearchButton.querySelector('.line')) {
        const line = document.createElement('span');
        line.classList.add('line');
        this.toggleSearchButton.appendChild(line);
      }

      this.searchTextInput.focus();
    } else {
      this.menu.classList.add('-closing');
    }
  }

  toggleSubMenu(e) {
    const clickedMenuItemExpandButton = e.currentTarget.querySelector('button');

    this.menuItemsWithSubMenu.forEach((menuItem) => {
      const menuItemExpandButton = menuItem.querySelector('button');
      if (
        menuItem.classList.contains('-sub-menu-show')
        && menuItem !== e.currentTarget
      ) {
        menuItem.classList.remove('-sub-menu-show');
        menuItemExpandButton.setAttribute('aria-expanded', false);
        const innerMenu = menuItem.lastChild;
        if (innerMenu) {
          innerMenu.style.maxHeight = 0;
        }
      }
    });

    e.currentTarget.classList.add('-sub-menu-show');
    clickedMenuItemExpandButton.setAttribute('aria-expanded', true);
    if (e.currentTarget.classList.contains('-sub-menu-show')) {
      const innerMenu = e.currentTarget.lastChild;
      if (innerMenu) {
        innerMenu.style.maxHeight = `${innerMenu.scrollHeight + 35}px`;
      }
    } else {
      const innerMenu = e.currentTarget.lastChild;
      if (innerMenu) {
        innerMenu.style.maxHeight = 0;
      }
    }
  }

  search(e) {
    e.preventDefault();

    if (this.searchTextInput.value === '') {
      this.searchTextInput.focus();
    } else {
      const { searchUrl } = this.searchForm.dataset;
      const searchQuery = encodeURIComponent(
        this.searchTextInput.value,
      ).replace(/%20/g, '+');

      window.location.href = `${searchUrl}?search=${searchQuery}`;
    }
  }

  changeLanguage() {
    const option = this.languageSwitcher.options[this.languageSwitcher.selectedIndex];

    if (option && option.value) {
      if (option.dataset.slug && window.pll_set_current_language) {
        window.pll_set_current_language(option.dataset.slug);
      }

      window.location.href = option.value;
    }
  }

  // Close sub menus if the user clicks outside of the menu.
  checkClickInMenu(e) {
    if (!e.target.closest('.-with-sub-menu')) {
      this.closeAllSubMenus();
    }
  }

  closeAllSubMenus() {
    this.menuItemsWithSubMenu.forEach((menuItem) => {
      const menuItemExpandButton = menuItem.querySelector('button');
      if (menuItem.classList.contains('-sub-menu-show')) {
        menuItem.classList.remove('-sub-menu-show');
        menuItemExpandButton.setAttribute('aria-expanded', false);
        const innerMenu = menuItem.lastChild;
        if (innerMenu) {
          innerMenu.style.maxHeight = 0;
        }
      }
    });
  }
}

domReady(() => {
  Menu.init();
});
